/**
 * Workshop Details Modal Component
 * Shows detailed information about a workshop
 */

import React, { useState } from 'react';
import { X, MapPin, Clock, User, DollarSign, Edit, History, ExternalLink } from 'lucide-react';
import { WorkshopPosition, WorkshopBasic, plantMappingUtils } from '../../services/plantMapping';
import { usePlantMappingStore } from '../../stores/plantMappingStore';
import { Link } from 'react-router-dom';

type WorkshopDetailsInput = WorkshopPosition | WorkshopBasic;

interface WorkshopDetailsModalProps {
  workshop: WorkshopDetailsInput;
  canEditLayout?: boolean;
  onClose: () => void;
}

const WorkshopDetailsModal: React.FC<WorkshopDetailsModalProps> = ({
  workshop,
  canEditLayout = false,
  onClose
}) => {
  const isPositionedWorkshop = 'workshop' in workshop;
  const workshopData = isPositionedWorkshop ? workshop.workshop : workshop;

  const [activeTab, setActiveTab] = useState<'details' | 'position' | 'history'>('details');
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    size: isPositionedWorkshop ? workshop.size : 24,
    color: isPositionedWorkshop ? (workshop.color || '') : '',
    zone: isPositionedWorkshop ? (workshop.zone || '') : ''
  });

  const { updatePosition, loading } = usePlantMappingStore();

  // Move workshop back to unpositioned queue
  const handleMoveToUnpositioned = async () => {
    if (!isPositionedWorkshop) return;
    try {
      await updatePosition(workshop.id, 0.05, 0.05, '');
      // Close modal after moving
      onClose();
    } catch (error) {
      console.error('Failed to move workshop to unpositioned:', error);
    }
  };

  const categoryColor = plantMappingUtils.getSQDCMCategoryColor(workshopData.sqdcm_category || '');
  const statusColor = plantMappingUtils.getWorkshopStatusColor(workshopData.status || '');

  const handleSaveEdit = async () => {
    if (!isPositionedWorkshop) return;
    try {
      await updatePosition(workshop.id, workshop.x, workshop.y, editData.zone);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update workshop:', error);
    }
  };

  const tabs = [
    { id: 'details', label: 'Details', icon: MapPin },
    { id: 'position', label: 'Position', icon: MapPin },
    { id: 'history', label: 'History', icon: History }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gray-50">
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold border-2"
              style={{
                backgroundColor: categoryColor,
                borderColor: statusColor
              }}
            >
              {workshopData.workshop_number}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Workshop #{workshopData.workshop_number}
              </h2>
              <p className="text-gray-600">{workshopData.title}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-96">
          {/* Details Tab */}
          {activeTab === 'details' && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <span 
                    className="inline-block px-3 py-1 rounded-full text-white text-sm font-medium"
                    style={{ backgroundColor: statusColor }}
                  >
                    {workshopData.status}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <span 
                    className="inline-block px-3 py-1 rounded-full text-white text-sm font-medium"
                    style={{ backgroundColor: categoryColor }}
                  >
                    {workshopData.sqdcm_category || 'N/A'}
                  </span>
                </div>
              </div>

              {/* Savings */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estimated Savings
                </label>
                <div className="flex items-center gap-2">
                  <DollarSign size={16} className="text-green-600" />
                  <span className="text-lg font-semibold text-green-600">
                    {plantMappingUtils.formatSavings(
                      workshopData.estimated_savings_per_year,
                      workshopData.estimated_savings_unit
                    )}
                  </span>
                </div>
              </div>

              {/* Links */}
              <div className="flex gap-3">
                <Link
                  to={`/workshops/${workshopData.id}`}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  <ExternalLink size={16} />
                  View Workshop Details
                </Link>
                <Link
                  to={`/workshops/${workshopData.id}/kata-board-v2`}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  <ExternalLink size={16} />
                  KATA Board
                </Link>
              </div>
            </div>
          )}

          {/* Position Tab */}
          {activeTab === 'position' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">Position Information</h3>
                {canEditLayout && isPositionedWorkshop && (
                  <button
                    onClick={() => setIsEditing(!isEditing)}
                    className="flex items-center gap-2 px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded"
                  >
                    <Edit size={14} />
                    {isEditing ? 'Cancel' : 'Edit'}
                  </button>
                )}
              </div>

              {!isPositionedWorkshop ? (
                <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded">
                  This workshop is currently in the unpositioned queue and has no saved canvas position yet.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Coordinates
                      </label>
                      <div className="text-sm text-gray-600">
                        X: {(workshop.x * 100).toFixed(1)}% ({workshop.absolute_x.toFixed(0)}px)
                      </div>
                      <div className="text-sm text-gray-600">
                        Y: {(workshop.y * 100).toFixed(1)}% ({workshop.absolute_y.toFixed(0)}px)
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Zone
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editData.zone}
                          onChange={(e) => setEditData(prev => ({ ...prev, zone: e.target.value }))}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="Zone name"
                        />
                      ) : (
                        <div className="text-sm text-gray-600">
                          {workshop.zone || 'No zone assigned'}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Size
                      </label>
                      {isEditing ? (
                        <input
                          type="number"
                          value={editData.size}
                          onChange={(e) => setEditData(prev => ({ ...prev, size: parseInt(e.target.value, 10) || 24 }))}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          min="12"
                          max="48"
                        />
                      ) : (
                        <div className="text-sm text-gray-600">{workshop.size}px</div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Custom Color
                      </label>
                      {isEditing ? (
                        <input
                          type="color"
                          value={editData.color}
                          onChange={(e) => setEditData(prev => ({ ...prev, color: e.target.value }))}
                          className="w-full h-8 border border-gray-300 rounded"
                        />
                      ) : (
                        <div className="flex items-center gap-2">
                          <div
                            className="w-4 h-4 rounded border"
                            style={{ backgroundColor: workshop.color || categoryColor }}
                          ></div>
                          <span className="text-sm text-gray-600">
                            {workshop.color || 'Default'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Last moved info */}
                  <div className="bg-gray-50 p-3 rounded">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <User size={14} />
                      <span>
                        Last moved by: {workshop.last_moved_by_name || 'Unknown'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                      <Clock size={14} />
                      <span>
                        {workshop.last_moved_at ? new Date(workshop.last_moved_at).toLocaleString() : 'N/A'}
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  {canEditLayout && (
                  <div className="flex justify-between items-center pt-4 border-t">
                    <button
                      onClick={handleMoveToUnpositioned}
                      disabled={loading}
                      className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50"
                    >
                      Move to Queue
                    </button>

                    {isEditing && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => setIsEditing(false)}
                          className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveEdit}
                          disabled={loading}
                          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                        >
                          {loading ? 'Saving...' : 'Save Changes'}
                        </button>
                      </div>
                    )}
                  </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Position History</h3>
              
              {/* Placeholder for position history */}
              <div className="text-center py-8 text-gray-500">
                <History size={48} className="mx-auto mb-2 opacity-50" />
                <p>Position history will be displayed here</p>
                <p className="text-sm">Track all movements and changes over time</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkshopDetailsModal;
